<template>
    <section class="booking-form-card" aria-labelledby="booking-client-title">
        <div class="booking-section-heading">
            <span class="booking-section-icon"><feather type="user" aria-hidden="true" /></span>
            <div>
                <h3 id="booking-client-title">{{ $t("reservationForm.client") }}</h3>
                <p>{{ $t("reservationForm.clientHint") }}</p>
            </div>
        </div>
        <div v-if="!client.id" class="booking-client-search">
            <label for="booking-client-search">{{ $t("reservationForm.searchClient") }}</label>
            <div class="booking-search-input">
                <feather type="search" aria-hidden="true" />
                <input
                    id="booking-client-search"
                    type="search"
                    class="form-control"
                    autocomplete="off"
                    v-model="query"
                    :placeholder="$t('reservationForm.searchPlaceholder')"
                    @input="scheduleSearch"
                    @keydown.enter.prevent="search" />
            </div>
            <p v-if="searching" class="booking-inline-status" role="status">{{ $t("reservationForm.searching") }}</p>
            <p v-else-if="searchError" class="text-danger" role="alert">
                {{ searchError }} <button type="button" class="booking-text-button" @click="search">{{ $t("reservationForm.retry") }}</button>
            </p>
            <ul v-else-if="results.length" class="booking-search-results" :aria-label="$tl('labels.searchResult')">
                <li v-for="result in results" :key="result.id">
                    <button type="button" @click="selectClient(result)">
                        <strong>{{ result.firstname }} {{ result.lastname }}</strong
                        ><span>{{ [result.company, result.email, result.tel].filter(Boolean).join(" · ") }}</span>
                    </button>
                </li>
            </ul>
            <p v-else-if="searched" class="booking-help" role="status">{{ $t("reservationForm.noClient") }}</p>
        </div>
        <div v-else class="booking-selected-client" role="status" aria-live="polite">
            <span class="booking-client-avatar" aria-hidden="true">{{ initials }}</span>
            <div class="booking-client-identity">
                <span class="booking-client-badge">{{ $t("reservationForm.selectedClient") }}</span>
                <strong class="booking-client-name">{{ displayName }}</strong>
                <small v-if="client.company && client.company !== displayName">{{ client.company }}</small>
                <small v-if="client.email || client.phone.raw">{{ [client.email, client.phone.raw].filter(Boolean).join(" · ") }}</small>
            </div>
            <button type="button" class="booking-text-button" @click="clearClient">{{ $t("reservationForm.changeClient") }}</button>
        </div>
        <details class="booking-client-details" :open="detailsOpen" @toggle="detailsOpen = $event.target.open">
            <summary ref="detailsSummary">
                <span>
                    <strong>{{ $t(client.id ? "reservationForm.clientCoordinates" : "reservationForm.newClient") }}</strong>
                    <small v-if="!client.id && displayName">{{ displayName }}</small>
                </span>
                <span class="booking-client-details-action">{{
                    $t(detailsOpen ? "reservationForm.collapseClient" : "reservationForm.expandClient")
                }}</span>
                <feather :type="detailsOpen ? 'chevron-up' : 'chevron-down'" aria-hidden="true" />
            </summary>
            <p v-if="client.id" class="booking-help">{{ $t("reservationForm.clientUpdate") }}</p>
            <div class="booking-client-fields">
                <div class="booking-field-full">
                    <label for="booking-civility">{{ $tl("labels.form.civility") }}<span v-if="mandatory.civility"> *</span></label>
                    <select
                        id="booking-civility"
                        name="civility"
                        v-model="civilityValue"
                        class="form-select"
                        :required="mandatory.civility"
                        :aria-invalid="Boolean(errors.civility)"
                        aria-describedby="booking-civility-error">
                        <option value="">—</option>
                        <option v-for="civility in ALL_CLIENT_CIVILITIES" :key="civility.value" :value="civility.value">
                            {{ capitalize(getClientCivilityLabelLong(civility.value)) }}
                        </option>
                    </select>
                    <small id="booking-civility-error" class="text-danger">{{ errors.civility?.[0] }}</small>
                </div>
                <div v-for="field in ['firstname', 'lastname', 'email', 'company']" :key="field">
                    <label :for="`booking-${field}`">{{ $tl(`labels.form.${field}`) }}<span v-if="mandatory[field]"> *</span></label>
                    <input
                        :id="`booking-${field}`"
                        :name="field"
                        :type="field === 'email' ? 'email' : 'text'"
                        v-model="client[field]"
                        class="form-control"
                        :required="mandatory[field]"
                        maxlength="190"
                        autocomplete="off"
                        :aria-invalid="Boolean(errors[field])"
                        :aria-describedby="`booking-${field}-error`"
                        @blur="field === 'email' && lookupContactMatches($event)" />
                    <small :id="`booking-${field}-error`" class="text-danger">{{ errors[field]?.[0] }}</small>
                </div>
                <div class="booking-field-full">
                    <label for="booking-phone">{{ $tl("labels.form.phoneNumber") }}<span v-if="mandatory.tel"> *</span></label>
                    <div class="booking-phone">
                        <select
                            id="booking-phone-country"
                            v-model="client.phone.country"
                            class="form-select"
                            :aria-label="$tl('labels.form.country')"
                            @blur="lookupContactMatches">
                            <option v-for="(name, code) in countryNames" :key="code" :value="code">{{ code }} · {{ name }}</option>
                        </select>
                        <input
                            id="booking-phone"
                            name="telInput"
                            type="tel"
                            autocomplete="off"
                            class="form-control"
                            v-model="client.phone.raw"
                            :required="mandatory.tel"
                            :aria-invalid="Boolean(errors.phoneNumber || errors.phoneCountry)"
                            aria-describedby="booking-phone-error"
                            @blur="lookupContactMatches" />
                    </div>
                    <small id="booking-phone-error" class="text-danger">{{ errors.phoneNumber?.[0] || errors.phoneCountry?.[0] }}</small>
                </div>
            </div>
            <div v-if="contactMatches.length" class="booking-help booking-contact-hint" role="status">
                <div class="d-flex align-items-center justify-content-between gap-2">
                    <span>{{ $t("reservationForm.contactMatch") }}</span>
                    <button type="button" class="booking-text-button p-2" @click="dismissContactMatches(true)">
                        {{ $t("reservationForm.dismissContactMatch") }}
                    </button>
                </div>
                <button
                    v-for="match in contactMatches"
                    :key="match.id"
                    type="button"
                    class="booking-text-button d-block py-2 text-start"
                    @click="selectClient(match)">
                    {{ $t("reservationForm.useContactMatch", { name: clientDisplayName(match) }) }}
                </button>
            </div>
            <small v-if="errors.client_id" class="text-danger" role="alert">{{ errors.client_id[0] }}</small>
            <details class="booking-details" :open="mandatory.type || Boolean(errors.type)">
                <summary>{{ $t("reservationForm.moreClient") }}</summary>
                <div class="booking-client-fields">
                    <div>
                        <label for="booking-client-locale">{{ $tl("labels.form.lang") }}</label
                        ><select id="booking-client-locale" class="form-select" v-model="client.locale">
                            <option v-for="lang in ALL_LANGS" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
                        </select>
                    </div>
                    <div>
                        <label for="client_type">{{ $tl("labels.clients.typeShort") }}<span v-if="mandatory.type"> *</span></label>
                        <SelectClientType :restaurant-id="Number(restaurantId)" v-model="client.type" :required="mandatory.type" small-screen />
                        <small class="text-danger">{{ errors.type?.[0] }}</small>
                    </div>
                    <label class="booking-checkbox"><input type="checkbox" v-model="client.is_vip" />{{ $tl("labels.clients.vip") }}</label>
                    <label class="booking-checkbox"
                        ><input type="checkbox" v-model="client.newsletter" />{{ $tl("labels.form.acceptNewsletter") }}</label
                    >
                </div>
            </details>
        </details>
    </section>
</template>

<script>
import countries from "i18n-iso-countries";
import ClientCivilityEnum from "../../../mixins/enums/ClientCivilityEnum";
import LangsEnum from "../../../mixins/enums/LangsEnum";
import SelectClientType from "../../forms/clients/selectClientType.vue";
import { clientDisplayName, createRequestGate, createReservationDraft, requiredClientFields } from "./reservationFormState.mjs";

export default {
    components: { SelectClientType },
    mixins: [ClientCivilityEnum, LangsEnum],
    props: {
        client: { type: Object, required: true },
        restaurantId: { required: true },
        fields: { type: Array, default: () => [] },
        requiredContact: { type: String, default: null },
        defaultCountry: { type: String, default: "FR" },
        errors: { type: Object, default: () => ({}) },
        api: { type: Object, required: true },
    },
    data: () => ({
        query: "",
        results: [],
        searching: false,
        searched: false,
        searchError: null,
        timer: null,
        searchGate: createRequestGate(),
        contactGate: createRequestGate(),
        contactMatches: [],
        checkedContactKey: null,
        dismissedContactKey: null,
        contactFieldId: "booking-email",
        detailsOpen: false,
    }),
    computed: {
        civilityValue: {
            get() {
                return this.client.civility ?? "";
            },
            set(value) {
                this.client.civility = value === "" ? null : value;
            },
        },
        displayName() {
            return clientDisplayName(this.client);
        },
        contactKey() {
            return JSON.stringify([this.client.email || "", this.client.phone.raw || "", this.client.phone.country || this.defaultCountry]);
        },
        initials() {
            return (
                [this.client.firstname, this.client.lastname]
                    .filter(Boolean)
                    .map((part) => part.trim()[0])
                    .join("")
                    .toUpperCase() || this.displayName.slice(0, 2).toUpperCase()
            );
        },
        mandatory() {
            return requiredClientFields(this.fields, this.requiredContact);
        },
        countryNames() {
            return countries.getNames(this.userLang === "fr" ? "fr" : "en");
        },
    },
    methods: {
        clientDisplayName,
        dismissContactMatches(restoreFocus = false) {
            this.contactGate.cancel();
            this.contactMatches = [];
            this.dismissedContactKey = this.contactKey;
            if (restoreFocus) this.$nextTick(() => this.$el.querySelector(`#${this.contactFieldId}`)?.focus({ preventScroll: true }));
        },
        async lookupContactMatches(event) {
            if (["booking-email", "booking-phone", "booking-phone-country"].includes(event?.target?.id)) this.contactFieldId = event.target.id;
            const key = this.contactKey;
            if (this.client.id || this.dismissedContactKey === key || this.checkedContactKey === key) return;
            const request = this.contactGate.begin();
            this.checkedContactKey = key;
            try {
                const matches = await this.api.matchingClients(
                    this.restaurantId,
                    { email: this.client.email, phoneNumber: this.client.phone.raw, phoneCountry: this.client.phone.country || this.defaultCountry },
                    request.signal
                );
                if (request.current() && !this.client.id && this.contactKey === key) this.contactMatches = matches;
            } catch {
                if (request.current()) this.checkedContactKey = null;
            }
        },
        scheduleSearch() {
            clearTimeout(this.timer);
            this.searchGate.cancel();
            this.results = [];
            this.searchError = null;
            this.searched = false;
            this.searching = false;
            if (this.query.trim().length >= 2) this.timer = setTimeout(() => this.search(), 350);
        },
        async search() {
            clearTimeout(this.timer);
            if (this.query.trim().length < 2) return;
            const request = this.searchGate.begin();
            this.searching = true;
            this.searchError = null;
            try {
                const data = await this.api.searchClients(this.restaurantId, this.query.trim(), request.signal);
                if (request.current()) {
                    this.results = Object.values(data ?? {});
                    this.searched = true;
                }
            } catch (error) {
                if (request.current()) this.searchError = this.$t("reservationForm.loadFailed");
            } finally {
                if (request.current()) this.searching = false;
            }
        },
        selectClient(client) {
            this.dismissContactMatches();
            this.searchGate.cancel();
            clearTimeout(this.timer);
            Object.assign(this.client, {
                id: client.id,
                civility: client.civility,
                firstname: client.firstname,
                lastname: client.lastname,
                company: client.company,
                email: client.email,
                newsletter: Boolean(client.newsletter),
                type: client.type,
                is_vip: Boolean(client.is_vip),
                locale: client.locale || this.userLang,
                phone: { raw: client.tel, country: client.tel_country || this.defaultCountry, isValid: null, value: null },
            });
            this.results = [];
            this.detailsOpen = false;
            this.$nextTick(() => this.$refs.detailsSummary?.focus());
        },
        clearClient() {
            this.dismissContactMatches();
            Object.assign(this.client, createReservationDraft(null, this.userLang).client);
            this.client.phone.country = this.defaultCountry;
            this.query = "";
            this.searched = false;
            this.searching = false;
            this.detailsOpen = false;
            this.$nextTick(() => this.$el.querySelector("input[type=search]")?.focus());
        },
    },
    watch: {
        contactKey() {
            this.contactGate.cancel();
            this.contactMatches = [];
            this.checkedContactKey = null;
            this.dismissedContactKey = null;
        },
        errors: {
            deep: true,
            handler(errors) {
                if (
                    Object.keys(errors).some((key) =>
                        ["civility", "firstname", "lastname", "email", "company", "phoneNumber", "phoneCountry", "type", "client_id"].includes(key)
                    )
                ) {
                    this.detailsOpen = true;
                }
            },
        },
    },
    mounted() {
        if (!this.client.phone.country) this.client.phone.country = this.defaultCountry;
    },
    beforeUnmount() {
        clearTimeout(this.timer);
        this.searchGate.cancel();
        this.contactGate.cancel();
    },
};
</script>
